import { useState } from "react";
import CalculatorForm from "@/components/CalculatorForm";
import ResultDisplay from "@/components/ResultDisplay";
import LeadForm from "@/components/LeadForm";
import ProductPage from "@/components/ProductPage";
import FAQ from "@/components/FAQ";

const Home = () => {
  const [step, setStep] = useState(1); // 1: form, 2: result, 3: product, 4: lead form
  const [calculationData, setCalculationData] = useState(null);
  const [result, setResult] = useState(null);

  const handleCalculate = (data, calcResult) => {
    setCalculationData(data);
    setResult(calcResult);
    setStep(2);
  };

  const handleShowProductPage = () => {
    setStep(3);
  };

  const handleShowLeadForm = () => {
    setStep(4);
  };

  const handleLeadSubmitted = () => {
    setStep(1);
    setCalculationData(null);
    setResult(null);
  };

  const handleReset = () => {
    setStep(1);
    setCalculationData(null);
    setResult(null);
  };

  const handleBackToResult = () => {
    setStep(2);
  };

  return (
    <div className="min-h-screen animated-gradient relative overflow-hidden">
      {/* Simplified background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{willChange: 'transform'}}>
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 right-1/3 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="py-6 px-4 sm:px-6 lg:px-8 border-b border-teal-500/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <a 
              href="https://alcodot.com.tr" 
              className="logo-container cursor-pointer"
              aria-label="Ana Sayfaya Dön"
            >
              <div className="logo-ring"></div>
              <div className="logo-ring"></div>
              <img 
                src="/images/alcodot-logo.png" 
                alt="Alcodot Logo" 
                className="logo-image h-12 w-auto"
              />
            </a>
            <a
              href="https://alcodot.com.tr"
              className="text-sm font-semibold text-teal-400 hover:text-teal-300 smooth-transition hover:drop-shadow-[0_0_10px_rgba(20,184,166,0.6)] flex items-center gap-2"
              data-testid="home-link"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="hidden sm:inline">Ana Sayfa</span>
            </a>
          </div>
        </header>

        {/* Hero Section */}
        {step === 1 && (
          <div className="text-center py-12 px-4 fade-in">
            {/* Trending Badge */}
            <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/30 rounded-full px-4 py-2 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
              </span>
              <span className="text-teal-300 text-sm font-semibold">Türkiye'nin En Çok Kullanılan Promil Hesaplayıcısı</span>
            </div>

            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black heading-primary mb-6 neon-text">
              <span className="text-gradient-glow">Alkol Promil</span>
              <br />
              <span className="text-white">Hesaplayıcı</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto font-medium mb-8">
              Widmark formülü ile profesyonel hesaplama.<br />
              Türkiye 2026 mevzuatına uygun bilgilendirme.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-3 mb-4">
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-700/50 rounded-xl px-4 py-2">
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-300 font-medium">Ücretsiz</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-700/50 rounded-xl px-4 py-2">
                <svg className="w-4 h-4 text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-300 font-medium">Gizli & Güvenli</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-700/50 rounded-xl px-4 py-2">
                <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-300 font-medium">Anında Sonuç</span>
              </div>
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-700/50 rounded-xl px-4 py-2">
                <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                </svg>
                <span className="text-sm text-gray-300 font-medium">2026 Mevzuatı</span>
              </div>
            </div>

            {/* Social Proof */}
            <p className="text-sm text-gray-500">
              <span className="text-teal-400 font-bold">10.000+</span> kişi bu aracı kullandı
            </p>
          </div>
        )}

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          {step === 1 && (
            <div className="slide-up">
              <CalculatorForm onCalculate={handleCalculate} />
            </div>
          )}

          {step === 2 && (
            <div className="slide-up">
              <ResultDisplay
                result={result}
                onShowLeadForm={handleShowLeadForm}
                onReset={handleReset}
              />
            </div>
          )}

          {step === 3 && (
            <div className="slide-up">
              <ProductPage
                calculationData={calculationData}
                result={result}
                onBack={handleBackToResult}
              />
            </div>
          )}

          {step === 4 && (
            <div className="slide-up">
              <LeadForm
                calculationData={calculationData}
                result={result}
                onSubmitted={handleLeadSubmitted}
                onBack={handleBackToResult}
              />
            </div>
          )}
        </main>

        {/* SSS Bölümü - Footer'dan önce */}
        {step === 1 && (
          <div className="relative z-10 bg-gradient-to-b from-transparent to-gray-50 py-16">
            <FAQ />
          </div>
        )}

        {/* Footer */}
        <footer className="py-8 px-4 text-center text-sm text-gray-500 border-t border-teal-500/10">
          <p className="mb-2">© 2025 Alcodot. Tüm hakları saklıdır.</p>
          <p className="text-xs text-gray-600">
            Bu hesaplama bilgilendirme amaçlıdır. Alkollü araç kullanmayın.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Home;